// WU-2.4 T6 — operator's own active evictions: fetch-on-mount + WS merge
// (useDirectedNotes model). Safe to mount multiple instances — the
// queue_eviction / queue_eviction_cleared events on the private per-user
// channel converge them without shared context. No client-side audience
// gate is needed (the private channel already scopes delivery server-side).
import { useEffect, useRef, useState, useCallback } from "react";
import type { WsClient } from "@/lib/ws-client";
import { evictionsApi, type MyEviction, type QueueEvictionEvent } from "../api/client";

// All-optional view of the canonical wire payload — this hook parses
// defensively (a malformed event is skipped, not crashed on).
type QueueEvictionPayload = Partial<QueueEvictionEvent>;

interface QueueEvictionClearedPayload {
  evictionId?: string;
}

export function useMyEvictions(wsClient: WsClient | null): {
  evictions: MyEviction[];
  refetch: () => Promise<void>;
} {
  const [evictions, setEvictions] = useState<MyEviction[]>([]);
  const inFlightRef = useRef(0);
  const tombstonesRef = useRef<Set<string>>(new Set());

  const refetch = useCallback(async () => {
    inFlightRef.current += 1;
    try {
      const res = await evictionsApi.mine();
      const tombstoned = tombstonesRef.current;
      setEvictions((prev) => {
        const fetched = res.evictions.filter((e) => !tombstoned.has(e.id));
        const fetchedIds = new Set(fetched.map((e) => e.id));
        const wsOnly = prev.filter((e) => !fetchedIds.has(e.id) && !tombstoned.has(e.id));
        return [...wsOnly, ...fetched];
      });
    } catch {
      // best-effort — badge/panel simply stay empty
    } finally {
      inFlightRef.current -= 1;
      // Window closes when no fetch is in flight — bounded set, and a
      // tombstoned id can't suppress unrelated future evictions (cuids never
      // recur, but don't rely on that). Swap in a FRESH Set rather than
      // mutating in place: the setEvictions updater above closes over
      // `tombstoned` and may run after this finally block (React defers the
      // updater), so clearing the same object in place would empty it out
      // from under that closure.
      if (inFlightRef.current === 0) tombstonesRef.current = new Set();
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!wsClient) return;
    const offNew = wsClient.onPayload("queue_eviction", (payload) => {
      const p = payload as QueueEvictionPayload;
      if (!p?.evictionId) return;
      // WS payload has no expiresAt — set null on insert; the next refetch
      // (mount/focus/whatever triggers it) corrects it from the server.
      const row: MyEviction = {
        id: p.evictionId,
        stepType: p.stepType ?? "",
        jobTypeId: p.jobTypeId ?? null,
        mode: p.mode ?? "immediate",
        reason: p.reason ?? "",
        expiresAt: null,
      };
      setEvictions((prev) => (prev.some((x) => x.id === row.id) ? prev : [row, ...prev]));
    });
    const offCleared = wsClient.onPayload("queue_eviction_cleared", (payload) => {
      const id = (payload as QueueEvictionClearedPayload)?.evictionId;
      if (!id) return;
      if (inFlightRef.current > 0) tombstonesRef.current.add(id);
      setEvictions((prev) => prev.filter((e) => e.id !== id));
    });
    return () => {
      offNew();
      offCleared();
    };
  }, [wsClient]);

  return { evictions, refetch };
}
